import React, { useState, useEffect, useRef } from 'react';
import './MobileNavBarOrganism.scss';

export interface MobileNavBarItem {
  id: string;
  icon?: string; // PrimeIcons class name (e.g., 'pi pi-home')
  label: string;
  onClick?: () => void;
  badge?: number; // Optional notification badge count
  isActive?: boolean;
  customRender?: () => React.ReactNode; // Custom component rendering (e.g., NotificationBell)
  isChevron?: boolean; // Special center chevron button
  chevronExpanded?: boolean; // State of chevron (up/down)
}

export interface MobileNavBarProps {
  items: MobileNavBarItem[];
  position?: 'top' | 'bottom' | 'both';
  className?: string;
  autoHideOnScroll?: boolean; // Enable auto-hide on scroll down
  scrollThreshold?: number; // Minimum scroll distance to trigger hide (default: 10px)
}

const MobileNavBarOrganism: React.FC<MobileNavBarProps> = ({
  items,
  position = 'bottom',
  className = '',
  autoHideOnScroll = true,
  scrollThreshold = 10,
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  useEffect(() => {
    if (!autoHideOnScroll) {
      return;
    }

    let rafId: number | null = null;

    const handleScroll = () => {
      if (!ticking.current) {
        rafId = globalThis.requestAnimationFrame(() => {
          const currentScrollY = globalThis.scrollY;
          const scrollDifference = currentScrollY - lastScrollY.current;

          // Only trigger if scroll difference is greater than threshold
          if (Math.abs(scrollDifference) > scrollThreshold) {
            if (scrollDifference > 0 && currentScrollY > 50) {
              // Scrolling down - hide bottom bar
              setIsVisible(false);
            } else if (scrollDifference < 0) {
              // Scrolling up - show bar
              setIsVisible(true);
            }
            lastScrollY.current = currentScrollY;
          }

          // Show bar at the very top
          if (currentScrollY <= 50) {
            setIsVisible(true);
          }

          ticking.current = false;
          rafId = null;
        });
        ticking.current = true;
      }
    };

    globalThis.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      globalThis.removeEventListener('scroll', handleScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [autoHideOnScroll, scrollThreshold]);

  // Reset visibility when navigating (route change detection)
  useEffect(() => {
    setIsVisible(true);
    lastScrollY.current = 0;
  }, [globalThis.location.pathname]);

  const renderNavBar = (positionClass: string) => {
    // Determine visibility class based on position and scroll state
    const visibilityClass = autoHideOnScroll && !isVisible ? 'mobile-nav-bar--hidden' : '';

    return (
      <nav className={`mobile-nav-bar mobile-nav-bar--${positionClass} ${visibilityClass} ${className}`}>
        <div className="mobile-nav-bar__container">
          {items.map((item) => {
            // If custom render is provided, use it instead
            if (item.customRender) {
              return (
                <div key={item.id} className="mobile-nav-bar__item mobile-nav-bar__item--custom">
                  {item.customRender()}
                </div>
              );
            }

            // Special chevron button rendering
            if (item.isChevron) {
              return (
                <button
                  key={item.id}
                  className="mobile-nav-bar__item mobile-nav-bar__item--chevron"
                  onClick={item.onClick}
                  aria-label={item.label}
                  type="button"
                >
                  <div className="mobile-nav-bar__chevron-button">
                    <i className={`pi pi-chevron-up ${item.chevronExpanded ? 'mobile-nav-bar__chevron-expanded' : ''}`} />
                    {item.badge !== undefined && item.badge > 0 && (
                      <span className="mobile-nav-bar__badge mobile-nav-bar__badge--chevron">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </div>
                </button>
              );
            }

            // Standard icon-based item
            return (
              <button
                key={item.id}
                className={`mobile-nav-bar__item ${item.isActive ? 'mobile-nav-bar__item--active' : ''}`}
                onClick={item.onClick}
                aria-label={item.label}
                type="button"
              >
                <div className="mobile-nav-bar__icon-wrapper">
                  {item.icon && <i className={`${item.icon}${item.isActive ? '-filled' : ''}`} />}
                  {item.badge !== undefined && item.badge > 0 && (
                    <span className="mobile-nav-bar__badge">
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </nav>
    );
  };

  return (
    <>
      {(position === 'top' || position === 'both') && renderNavBar('top')}
      {(position === 'bottom' || position === 'both') && renderNavBar('bottom')}
    </>
  );
};

export default MobileNavBarOrganism;
